import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import {
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_GZ_SELECT,
  WEB_COMPONENT_GZ_TITLE,
} from 'apps/webcomponents/src/webcomponents.constants';
import { GridViewWebComponent } from 'apps/webcomponents/src/grid/gridView';
import {
  ComputersAndVirtualMachinesGridDataProvider,
} from '../dataProvider/ComputersAndVirtualMachinesGridDataProvider';
import { GzSelectWebComponent } from 'apps/webcomponents/src/ui/gzSelect';
import {
  Column,
  ColumnKeys,
  ComputersAndVirtualMachinesViewData,
  Dependencies,
  RETRIEVE_ERROR_CODES,
  DEFAULT_VISIBLE_COLUMNS,
  SUBMIT_ERROR_CODES,
} from '../types';
import { GridConfig } from './GridConfig';
import { GridViewMetadata, ViewType } from '../../../grid/gridView/types';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { GzTooltipWebComponent } from '../../../ui/gzTooltip';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { translateFn } from '../../../utils/translator.helper';
import { GzBackendError } from '../../../accounts/common/types/GzBackendError';

export class ComputersAndVirtualMachinesView extends BaseWebComponentView {
  public dom: {
    $grid: GridViewWebComponent<Column, ColumnKeys>;
    $select: GzSelectWebComponent;
    $addExclusionsBtn: HTMLElement;
    $restoreBtn: HTMLElement;
    $retrieveBtn: GzButtonWebComponent;
    $retrieveTooltip: GzTooltipWebComponent;
    $emptyQuarantineBtn: HTMLElement;
    $deleteTaskBtn: HTMLElement;
    $downloadBtn: HTMLElement;
    $title: HTMLElement;
    $hostElement: HTMLElement;
  };

  public dependencies: Dependencies;

  public getDomElements(): void {
    this.dom.$grid = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW);
    this.dom.$select = this.shadow.querySelector(WEB_COMPONENT_GZ_SELECT);
    this.dom.$title = this.shadow.querySelector(WEB_COMPONENT_GZ_TITLE);
    this.dom.$addExclusionsBtn = this.shadow.querySelector('[data-add-exclusions]');
    this.dom.$restoreBtn = this.shadow.querySelector('[data-restore]');
    this.dom.$retrieveBtn = this.shadow.querySelector('#retrieve');
    this.dom.$retrieveTooltip = this.shadow.querySelector('#retrieve-tooltip');
    this.dom.$emptyQuarantineBtn = this.shadow.querySelector('#empty-quarantine');
    this.dom.$deleteTaskBtn = this.shadow.querySelector('#delete-task');
    this.dom.$downloadBtn = this.shadow.querySelector('#download');
    this.dom.$hostElement = this.shadow;
  }

  public buildView({ response }: { response: ComputersAndVirtualMachinesViewData }): void {
    const { viewMetaData, canHaveCompanyDownload } = new GridConfig().getDataForView(
      this.dependencies,
      this.dom,
      response.externalFilters
    );

    toggleVisibility(this.dom.$downloadBtn, canHaveCompanyDownload);

    this.buildGrid(viewMetaData);
    this.setTranslations();
    this.toggleRetrieveVisibility();
  }

  private toggleRetrieveVisibility(): void {
    toggleVisibility(this.dom.$retrieveTooltip,  Application.Main.product.isCloud());
  }

  public enableRestore(enabled: boolean): void {
    this.enableDisableElement(this.dom.$restoreBtn, enabled);
  }

  public enableDownload(enabled: boolean): void {
    this.enableDisableElement(this.dom.$downloadBtn, enabled);
  }

  public enableDelete(enabled: boolean): void {
    this.enableDisableElement(this.dom.$deleteTaskBtn, enabled);
  }

  public enableRetrieveButton(enabled: boolean): void {
    this.enableDisableElement(this.dom.$retrieveBtn, enabled);
  }

  public enableAddExclusions(enabled: boolean): void {
    this.enableDisableElement(this.dom.$addExclusionsBtn, enabled);
  }

  public enableEmptyQuarantine(enabled: boolean) {
    this.enableDisableElement(this.dom.$emptyQuarantineBtn, enabled);
  }

  private enableDisableElement(element, enabled: boolean): void {
    const enabledToMethod = {
      true: 'removeAttribute',
      false: 'setAttribute',
    };

    element[enabledToMethod[String(enabled)]]('disabled', '');
  }

  private buildGrid(viewMetaData: GridViewMetadata<Column, ColumnKeys>): void {
    const grid = this.dom.$grid;

    grid.propertiesSet = false;

    grid.dataProvider = new ComputersAndVirtualMachinesGridDataProvider(this.dependencies);
    grid.sideMenuParent = this.dom.$hostElement;

    for (const [key, value] of Object.entries(viewMetaData)) {
      grid[key] = value;
    }

    grid.defaultViewProps = {
      compactView: viewMetaData.isCompactViewChecked,
      displayedColumns: DEFAULT_VISIBLE_COLUMNS,
      filters: viewMetaData.filters,
      name: '',
      pageSize: viewMetaData.pageSize,
      type: ViewType.DEFAULT,
      id: viewMetaData.viewId,
    };

    grid.propertiesSet = true;

    grid.injectDependencies(this.dependencies);
    grid.start().catch();
  }

  public setTooltipRetrieve(msg: string): void {
    this.dom.$retrieveTooltip.tooltipText = msg;
  }

  public setTooltipErrorCodesTranslated(error: GzBackendError): void {
    const err = error.err;
    if (!err.hasOwnProperty('code')) {
      this.setTooltipRetrieve(translateFn('A general error occurred. Please try again later or contact Bitdefender Enterprise Support for more information.'));
      return;
    }
    switch (err.code) {
      case RETRIEVE_ERROR_CODES.MA_DISABLED_ERROR_CODE:
        this.setTooltipRetrieve(translateFn('Your company does not meet all security criteria to retrieve files.'));
        break;
      case RETRIEVE_ERROR_CODES.USER_WITH_2FA_DISABLE_ERROR_CODE:
        this.setTooltipRetrieve(translateFn('Two-factor authentication must be enabled for your account.'));
        break;
      case RETRIEVE_ERROR_CODES.ENDPOINT_UNMANAGED_ERROR_CODE:
        this.setTooltipRetrieve(translateFn('The endpoint you are trying to retrieve the file from is unmanaged and requires security agent installation.'));
        break;
      case RETRIEVE_ERROR_CODES.OS_NOT_SUPPORTED:
        this.setTooltipRetrieve(translateFn('The endpoint operating system is unsupported.'));
        break;
      case RETRIEVE_ERROR_CODES.QUARANTINE_NUMBER_OF_RETRIEVED_FILES_LIMIT_REACHED:
        this.setTooltipRetrieve(translateFn('You can retrieve up to 10 files per company. Delete previously retrieved files and try again.'));
        break;
      case RETRIEVE_ERROR_CODES.COMPANY_IS_NOT_AVAILABLE_FOR_QUARANTINE_RETRIEVE:
        this.setTooltipRetrieve(translateFn('You require permission to retrieve and download quarantined files.'));
        break;
      default:
        this.setTooltipRetrieve(translateFn('A general error occurred. Please try again later or contact Bitdefender Enterprise Support for more information.'));
    }
  }

  public getSubmitToLabsCodesTranslated(error: GzBackendError): string {
    const err = error.err;
    if (!err.hasOwnProperty('code')) {
      return translateFn('Failed to submit file');
    }
    switch (err.code) {
      case SUBMIT_ERROR_CODES.INCORRECT_ARCHIVE_PASSWORD:
        return translateFn('Failed to submit file, incorrect password');
      default:
        return translateFn('Failed to submit file');
    }
  }

  private setTranslations(): void {
    this.dom.$addExclusionsBtn.innerText = this.dependencies.translateFn('Add exclusions');
    this.dom.$restoreBtn.textContent = this.dependencies.translateFn('Restore');
    this.dom.$retrieveBtn.textContent = this.dependencies.translateFn('Retrieve');
    this.dom.$emptyQuarantineBtn.textContent = this.dependencies.translateFn('Empty Quarantine');
    this.dom.$deleteTaskBtn.textContent = this.dependencies.translateFn('Delete');
    this.dom.$downloadBtn.textContent = this.dependencies.translateFn('Download');
    this.dom.$title.textContent = this.dependencies.translateFn('Computers and Virtual Machines');
  }
}
